import { useRef, type CSSProperties, type ReactNode } from "react";

type SpotlightCardProps = {
  children: ReactNode;
  /** RGB bez "rgb(...)", np. "190, 242, 100" */
  color?: string;
  /** Promień światła w px */
  radius?: number;
  className?: string;
  style?: CSSProperties;
};

/**
 * Owija istniejącą kartę (np. jedną z 4 kart advantage) i dodaje
 * radialny gradient podążający za kursorem. Nie zmienia treści karty -
 * tylko dokłada warstwę tła i nasłuch mousemove na samym wrapperze.
 *
 * Wymaga, żeby karta miała już ramkę/tło (jak obecne karty z border white/10) -
 * spotlight nakłada się pod treścią, dzięki position: relative + z-index.
 *
 * Użycie:
 *   <SpotlightCard>
 *     <div className="border border-white/10 p-6">...istniejąca treść...</div>
 *   </SpotlightCard>
 */
export function SpotlightCard({ children, color = "190, 242, 100", radius = 500, className, style }: SpotlightCardProps) {
  const ref = useRef<HTMLDivElement>(null);

  function handlePointerMove(e: React.PointerEvent<HTMLDivElement>) {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    el.style.setProperty("--spotlight-x", `${x}px`);
    el.style.setProperty("--spotlight-y", `${y}px`);
  }

  function handlePointerEnter() {
    ref.current?.style.setProperty("--spotlight-opacity", "1");
  }

  function handlePointerLeave() {
    ref.current?.style.setProperty("--spotlight-opacity", "0");
  }

  return (
    <div
      ref={ref}
      className={className}
      style={{
        position: "relative",
        ...style,
      }}
      onPointerMove={handlePointerMove}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
    >
      {/* Warstwa światła - pod treścią, ponad tłem karty */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "inherit",
          opacity: "var(--spotlight-opacity, 0)",
          transition: "opacity 300ms ease",
          background: `radial-gradient(${radius}px circle at var(--spotlight-x, 50%) var(--spotlight-y, 50%), rgba(${color}, 0.06), transparent 60%)`,
          pointerEvents: "none",
        }}
      />
      {/* Treść karty - musi być nad warstwą światła */}
      <div style={{ position: "relative", zIndex: 1 }}>{children}</div>
    </div>
  );
}
